import { FluentProvider, makeStaticStyles, makeStyles, webDarkTheme } from '@fluentui/react-components';
import { type PropsWithChildren, useMemo } from 'react';

import { usePreferences } from '../preferences';
import { cssVarsFor } from './themeVars';
import { theme as appLightTheme } from './tokens';

export const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const classes = useStyles();
    const { preferences } = usePreferences();
    useGlobalStyles();

    // The CSS variables for the active mode are applied inline to the
    // FluentProvider's wrapper element — this means switching `themeMode`
    // re-paints the entire app via cascade without remounting anything.
    const cssVars = useMemo(() => cssVarsFor(preferences.themeMode), [preferences.themeMode]);

    const fluentTheme =
        preferences.themeMode === 'dark' ? { ...webDarkTheme, fontFamilyBase: appLightTheme.fontFamilyBase } : appLightTheme;

    return (
        <FluentProvider
            theme={fluentTheme}
            className={classes.container}
            style={cssVars as React.CSSProperties}
            applyStylesToPortals={true}
        >
            {children}
        </FluentProvider>
    );
};

const useStyles = makeStyles({
    container: {
        display: 'contents',
    },
});

const useGlobalStyles = makeStaticStyles({
    body: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        margin: 0,
        fontFamily: appLightTheme.fontFamilyBase,
        fontVariant: 'lining-nums',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    },
    '#root': {
        display: 'contents',
    },
    ':focus-visible': {
        outline: `2px solid ${appLightTheme.customPaletteGreen}`,
        outlineOffset: '1px',
    },
    'input:focus-visible': {
        outline: 'none',
    },
});
